import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Baby, School, BookOpen, Utensils, PlaySquare, Laptop, HelpCircle, Palette } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_1f5fe23d-d8f2-4d46-9a46-cbe5186179e5/artifacts/funyv5ek_0A13FCB2-C238-41B7-9C18-1C12F06E5487.png';

const programs = [
  {
    id: 'early',
    title: 'Early Learners Program',
    ageRange: 'Ages 1-5 Years',
    schedule: 'Morning 7AM - 3PM',
    image: 'https://static.prod-images.emergentagent.com/jobs/9dbc40b3-470e-44c2-bbfd-5443f4c2d978/images/3eed6a63b61fe5f80c70ce0eb4588f4068a4c0eb4321982ce46a634bff75e5cf.png',
    icon: Baby,
    color: 'primary',
    features: [
      { icon: BookOpen, text: 'Preschool Curriculum' },
      { icon: Utensils, text: 'Healthy Meals & Snacks' },
      { icon: PlaySquare, text: 'Indoor & Outdoor Play' },
    ],
    description: 'Our early learners program focuses on foundational skills through play-based learning, helping young children develop social, emotional, and cognitive abilities in a nurturing environment.',
  },
  {
    id: 'after',
    title: 'After School Program',
    ageRange: 'Ages 6-10 Years',
    schedule: 'Afternoon 3PM - 8PM',
    image: 'https://static.prod-images.emergentagent.com/jobs/9dbc40b3-470e-44c2-bbfd-5443f4c2d978/images/61c7b921f75f748105e548e51c3a4ad685aca45cc7c46aa585cc99d418e62ed0.png',
    icon: School,
    color: 'secondary',
    features: [
      { icon: Laptop, text: 'Learning Activities' },
      { icon: HelpCircle, text: 'Homework Help' },
      { icon: Palette, text: 'Fun & Enriching Program' },
    ],
    description: 'Our after school program provides a structured yet fun environment where school-aged children can complete homework, engage in educational activities, and participate in enrichment programs.',
  },
];

const colorMap = {
  primary: { bg: 'bg-primary', bgLight: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' },
  secondary: { bg: 'bg-secondary', bgLight: 'bg-secondary/10', text: 'text-secondary', border: 'border-secondary/20' },
};

const MobileHome = () => {
  const [activeProgram, setActiveProgram] = useState('early');
  const current = programs.find(p => p.id === activeProgram);
  const cm = colorMap[current.color];
  const IconComp = current.icon;

  return (
    <div className="pb-6" data-testid="mobile-home">
      {/* Hero Image */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="relative w-full">
        <img src={HERO_IMAGE} alt="Hooyo's Place Early Education & Childcare" className="w-full h-auto object-contain" data-testid="mobile-hero-image" />
        <div className="absolute top-0 left-0 bottom-0 w-[12%] bg-gradient-to-r from-background to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 bottom-0 w-[12%] bg-gradient-to-l from-background to-transparent pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-[8%] bg-gradient-to-b from-background to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-[15%] bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </motion.div>

      {/* Welcome Text */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }} className="px-6 -mt-4 text-center space-y-3">
        <h1 className="font-fredoka font-bold text-2xl text-foreground" data-testid="mobile-home-title">
          Welcome to <span className="text-primary">Hoo</span><span className="text-secondary">yo's</span><span className="text-accent"> Place</span>
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
          A warm, nurturing home where every child is celebrated. We provide quality early education and childcare in a safe, loving environment where little minds grow.
        </p>
      </motion.div>

      {/* Programs Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }} className="px-4 mt-8">
        {/* Section Header */}
        <div className="text-center mb-4">
          <span className="inline-block bg-primary/12 text-primary text-xs font-bold px-4 py-1.5 rounded-full tracking-wide uppercase">Our Programs</span>
          <h2 className="font-fredoka font-bold text-xl mt-2" data-testid="mobile-programs-heading">
            <span className="text-primary">Programs</span> <span className="text-secondary">for Every</span> <span className="text-accent"> Age</span>
          </h2>
        </div>

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-4" data-testid="mobile-program-tabs">
          {programs.map(p => {
            const cm2 = colorMap[p.color];
            return (
              <button
                key={p.id}
                onClick={() => setActiveProgram(p.id)}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all ${activeProgram === p.id ? `${cm2.bg} text-white shadow-soft` : `bg-white ${cm2.text} border border-border/40`}`}
                data-testid={`mobile-program-tab-${p.id}`}
              >
                {p.ageRange}
              </button>
            );
          })}
        </div>

        {/* Program Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -15 }}
            transition={{ duration: 0.25 }}
            className="bg-white rounded-2xl overflow-hidden shadow-soft border border-border/30"
            data-testid={`mobile-program-card-${current.id}`}
          >
            {/* Image */}
            <div className="relative h-44 overflow-hidden">
              <img src={current.image} alt={current.title} className="w-full h-full object-cover" style={{ objectPosition: current.id === 'early' ? '50% 45%' : 'center' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent" />
              <div className="absolute top-3 right-3 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-soft">
                <IconComp className={`w-5 h-5 ${cm.text}`} />
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              <div>
                <h3 className={`font-fredoka font-bold text-lg ${cm.text}`} data-testid="mobile-program-title">{current.title}</h3>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Badge className={`${cm.bgLight} ${cm.text} ${cm.border} rounded-full px-2.5 py-0.5 text-[10px] font-bold`}>{current.ageRange}</Badge>
                  <Badge className={`${cm.bgLight} ${cm.text} ${cm.border} rounded-full px-2.5 py-0.5 text-[10px] font-bold`}>{current.schedule}</Badge>
                </div>
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed">{current.description}</p>

              {/* Features */}
              <div className="space-y-2">
                {current.features.map((f, i) => {
                  const FIcon = f.icon;
                  return (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${cm.bgLight} flex items-center justify-center flex-shrink-0`}>
                        <FIcon className={`w-3.5 h-3.5 ${cm.text}`} />
                      </div>
                      <span className="text-xs text-foreground/80 font-medium">{f.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Enroll Button */}
              <Link to="/mobile/register">
                <Button className={`w-full ${cm.bg} hover:opacity-90 text-white rounded-xl py-4 font-bold text-sm shadow-soft mt-1`} data-testid={`mobile-program-enroll-${current.id}`}>
                  Enroll in This Program
                </Button>
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default MobileHome;
